import { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Stack,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Fade,
  Tab,
  LinearProgress,
  Divider,
} from "@mui/material";
import { getApplicantsList } from "store/slices/employer/applicants/all-employer-applicants-slice";
import { useAppDispatch, useAppSelector } from "helpers/hooks";
import { useNavigate } from "react-router-dom";
import { getAccInfo } from "store/slices/employer/settingsTs/acc-info-slice";
import { markCandidateAsReviewed } from "store/slices/employer/applicants/applicants-list-slice";
import { SharedApplicantsList } from "./SharedApplicantsList";
import { ShTabs } from "shared/SharedStyles/ShNavigation";

export const SharedApplicants = () => {
  const navigate = useNavigate(); 
  const [searchTerm, setSearchTerm] = useState("");
  const [timeFilter, setTimeFilter] = useState("1m");
  const [tabIndex, setTabIndex] = useState(0);  
  const { applicantsList, paginationParams, getApplicantsStatus } = useAppSelector((state) => state.employer.applicants.allEmployerApplicants);
  const { employerDetails } = useAppSelector((state) => state.employer.employerDetails);
  const dispatch = useAppDispatch();
  const { accountInfo } = useAppSelector((state) => state.employer.employerSettings.accInfo);

  useEffect(() => {
    dispatch(getAccInfo());
  }, [dispatch]);

  const calculateDateRange = useCallback(() => {
    const now = new Date();
    let startDate: string | undefined;
    let endDate = now.toISOString().split("T")[0]; // Today's date in YYYY-MM-DD format
  
    switch (timeFilter) {
      case "1m":
        startDate = new Date(now.setMonth(now.getMonth() - 1)).toISOString().split("T")[0];
        break;
      case "3m":
        startDate = new Date(now.setMonth(now.getMonth() - 3)).toISOString().split("T")[0];
        break;
      case "6m":
        startDate = new Date(now.setMonth(now.getMonth() - 6)).toISOString().split("T")[0];
        break;
      case "1y":
        startDate = new Date(now.setFullYear(now.getFullYear() - 1)).toISOString().split("T")[0];
        break;
      case "all":
      default:
        startDate = undefined;
        break;
    }
  
    return { startDate, endDate };
  }, [timeFilter]);
  
  useEffect(() => {
    const { startDate, endDate } = calculateDateRange();

    if (employerDetails?.id) {
      const employerId = employerDetails.id;
      dispatch(
        getApplicantsList({
          employerId,
          startDate,
          endDate,
          pageNo: paginationParams.requestedPageNumber || 1,
          pageSize: paginationParams.requestedPageSize || 50,
        })
      );
    }
  }, [calculateDateRange, timeFilter, employerDetails?.id, dispatch, paginationParams.requestedPageNumber, paginationParams.requestedPageSize]);

  const assignedApplicants = applicantsList.filter(
    (applicant) => 
      Array.isArray(applicant.shared_with_account_ids) && 
      applicant.shared_with_account_ids.includes(accountInfo?.id) && 
      !(Array.isArray(applicant.reviewed_by_account_ids) && applicant.reviewed_by_account_ids.includes(accountInfo?.id))
  );

  const reviewedApplicants = applicantsList.filter(
    (applicant) => Array.isArray(applicant.reviewed_by_account_ids) && applicant.reviewed_by_account_ids.includes(accountInfo?.id)
  );

  const filteredAssignedApplicants = assignedApplicants.filter((applicant) => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      applicant.candidate.account.first_name.toLowerCase().includes(searchTermLower) ||
      applicant.candidate.account.last_name.toLowerCase().includes(searchTermLower) ||
      applicant.candidate.account.email.toLowerCase().includes(searchTermLower) ||
      applicant.employerjob.name.toLowerCase().includes(searchTermLower)
    );
  });

  const filteredReviewedApplicants = reviewedApplicants.filter((applicant) => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      applicant.candidate.account.first_name.toLowerCase().includes(searchTermLower) ||
      applicant.candidate.account.last_name.toLowerCase().includes(searchTermLower) ||
      applicant.candidate.account.email.toLowerCase().includes(searchTermLower) ||
      applicant.employerjob.name.toLowerCase().includes(searchTermLower)
    );
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    if (employerDetails?.id) {
      const employerId = employerDetails.id;
      dispatch(
        getApplicantsList({
          employerId,
          startDate: calculateDateRange().startDate,
          endDate: calculateDateRange().endDate,
          pageNo: value,
          pageSize: 1000,
        })
      );
    }
  };

  const handleViewProfile = (jobId: number, applicantId: number, viewResume: boolean) => {
    const url = viewResume
      ? `/employer/job/${jobId}/applicant/${applicantId}/details#resume`
      : `/employer/job/${jobId}/applicant/${applicantId}/details`;

    navigate(url);
  };

  const handleMarkingCandidateAsReviewed = (applicantId: number) => {
    if (applicantId && accountInfo?.id) {
      dispatch(
        markCandidateAsReviewed({
          applicantId: applicantId,
          accountId: accountInfo?.id,
        })
      );
    }
  };

  return (
    <Fade in={true} timeout={800}>
      <Stack rowGap={3} padding={{ xs: 1, sm: 1, md: 2, lg: 2 }}>
        {getApplicantsStatus === "pending" && <LinearProgress />}
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
          <Stack>
            <Typography variant="h6">My Applicant Center</Typography>
            <Typography variant="body2" color="gray" maxWidth={600}>
              The Applicant Center helps you manage and track candidates efficiently.
              Use this feature to review candidates shared by other members of your company.
            </Typography>
          </Stack>
          <Divider flexItem/>
          <Stack direction="row" spacing={1}>
            <TextField
              label="Search by Name, Job Name, or Email"
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: 325 }}
            />
            <Stack direction="row" spacing={4} justifyContent="flex-start" alignItems="center">
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Filter by Time</InputLabel>
                <Select value={timeFilter} onChange={(e) => setTimeFilter(e.target.value)} label="Filter by Time">
                  <MenuItem value="1m">Last 1 Month</MenuItem>
                  <MenuItem value="3m">Last 3 Months</MenuItem>
                  <MenuItem value="6m">Last 6 Months</MenuItem>
                  <MenuItem value="1y">Last 1 Year</MenuItem>
                  <MenuItem value="all">All Time</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </Stack>

          <ShTabs value={tabIndex} onChange={handleTabChange} indicatorColor="primary" textColor="primary" highlightVariant="underline">
            <Tab label="Assigned Applicants" />
            <Tab label="Reviewed Applicants" />
          </ShTabs>

        {tabIndex === 0 ? (
          // Assigned Applicants Tab with Search Filter
          <SharedApplicantsList
            applicants={filteredAssignedApplicants}
            handleViewProfile={handleViewProfile}
            handleMarkingCandidateAsReviewed={handleMarkingCandidateAsReviewed}
            paginationParams={paginationParams}
            handlePageChange={handlePageChange}
          />
        ) : (
          // Reviewed Applicants Tab with Search Filter
          <SharedApplicantsList
            applicants={filteredReviewedApplicants}
            handleViewProfile={handleViewProfile}
            handleMarkingCandidateAsReviewed={handleMarkingCandidateAsReviewed}
            paginationParams={paginationParams}
            handlePageChange={handlePageChange}
          />
        )}
      </Stack>
    </Fade>
  );
};
